/**
 * Chrome + primitives for the static tool examples.
 * ---------------------------------------------------------------------------
 * These pieces exist so the landing-page examples read like real screens from
 * inside each tool: the same tab rows, card surfaces, score bands and rings the
 * product uses. Everything here is static presentation — no data, no state.
 * Colours come from semantic tokens only.
 */
import React from 'react';
import { cn } from '@/lib/utils';

/** Tool tab row, mirroring the in-product navigation of that tool. */
export const AppShell: React.FC<{
  tabs: string[];
  active?: number;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}> = ({ tabs, active = 0, toolbar, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-1 overflow-hidden rounded-lg bg-muted/30 p-1">
      {tabs.map((t, i) => (
        <span
          key={t}
          className={cn(
            'rounded-md px-2.5 py-1 text-[11px] whitespace-nowrap',
            i === active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
          )}
        >
          {t}
        </span>
      ))}
    </div>
    {toolbar && <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">{toolbar}</div>}
    {children}
  </div>
);

/** Small section title used inside the product's panels. */
export const PanelTitle: React.FC<{ title: string; meta?: string }> = ({ title, meta }) => (
  <div className="flex items-baseline justify-between gap-3">
    <h3 className="text-sm font-medium text-foreground">{title}</h3>
    {meta && <span className="text-[11px] text-muted-foreground">{meta}</span>}
  </div>
);

/** Score → semantic band, mapping the product's green/yellow/red thresholds. */
export function scoreBand(score: number): { text: string; bg: string; fill: string; label: string } {
  if (score >= 80) return { text: 'text-success', bg: 'bg-success/10', fill: 'bg-success', label: 'Great match' };
  if (score >= 60) return { text: 'text-warning', bg: 'bg-warning/10', fill: 'bg-warning/70', label: 'Good match' };
  return { text: 'text-destructive', bg: 'bg-destructive/10', fill: 'bg-destructive/60', label: 'Worth a look' };
}

/** The product's labelled meter row. */
export const MeterRow: React.FC<{ label: string; value: number; band?: string; tone?: string }> = ({
  label,
  value,
  band,
  tone,
}) => (
  <div className="space-y-1.5">
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-foreground">{label}</span>
      <span className="text-muted-foreground tabular-nums">{band ? `${band} · ${value}` : `${value}%`}</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
      <div className={cn('h-full rounded-full', tone ?? scoreBand(value).fill)} style={{ width: `${value}%` }} />
    </div>
  </div>
);

/** Circular fit ring, as used on the compatibility report header. */
export const FitRing: React.FC<{ value: number; caption?: string; tone?: string }> = ({
  value,
  caption,
  tone = 'text-primary',
}) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-[68px] h-[68px]">
        <svg viewBox="0 0 68 68" className="w-full h-full -rotate-90">
          <circle cx="34" cy="34" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="5" />
          <circle
            cx="34"
            cy="34"
            r={r}
            fill="none"
            stroke="currentColor"
            className={tone}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${(c * value) / 100} ${c}`}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display text-lg text-foreground tabular-nums">
          {value}
        </span>
      </div>
      {caption && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{caption}</span>}
    </div>
  );
};

/** Static five-axis radar, matching the team Big Five chart's shape. */
export const RadarMini: React.FC<{
  data: { label: string; value: number }[];
  tone?: string;
}> = ({ data, tone = 'hsl(var(--secondary))' }) => {
  const size = 190;
  const cx = size / 2;
  const cy = size / 2 + 4;
  const R = 62;
  const point = (i: number, v: number) => {
    const angle = (Math.PI * 2 * i) / data.length - Math.PI / 2;
    const rr = (R * v) / 100;
    return [cx + rr * Math.cos(angle), cy + rr * Math.sin(angle)] as const;
  };
  const poly = data.map((d, i) => point(i, d.value).join(',')).join(' ');
  return (
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[220px]" role="img" aria-label="Team profile radar">
        {[100, 75, 50, 25].map((ring) => (
          <polygon
            key={ring}
            points={data.map((_, i) => point(i, ring).join(',')).join(' ')}
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth="0.7"
          />
        ))}
        {data.map((_, i) => {
          const [x, y] = point(i, 100);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="hsl(var(--border))" strokeWidth="0.5" />;
        })}
        <polygon points={poly} fill={tone} fillOpacity="0.28" stroke={tone} strokeWidth="1.5" />
        {data.map((d, i) => {
          const [x, y] = point(i, 118);
          return (
            <text
              key={d.label}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="8.5"
              fill="hsl(var(--muted-foreground))"
            >
              {d.label}
            </text>
          );
        })}
      </svg>
      <div className="grid w-full grid-cols-5 gap-1 pt-1">
        {data.map((d) => (
          <div key={d.label} className="text-center">
            <p className="text-[11px] font-medium text-foreground tabular-nums">{d.value}</p>
            <p className="text-[9px] text-muted-foreground">{d.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Poster-style media card, matching the Liker swipe card composition. */
export const PosterCard: React.FC<{
  title: string;
  meta: string;
  score: number;
  arm: string;
  tags: string[];
  why: string;
}> = ({ title, meta, score, arm, tags, why }) => (
  <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-card">
    <div className="relative h-44 bg-gradient-to-br from-success/35 via-muted to-background">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--foreground)/0.12),transparent_60%)]" aria-hidden />
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-card via-card/80 to-transparent" aria-hidden />
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        <span className="rounded-full border border-success/30 bg-success/15 px-2 py-0.5 text-[10px] text-success">
          {score}% · {scoreBand(score).label}
        </span>
        <span className="rounded-full border border-info/30 bg-info/15 px-2 py-0.5 text-[10px] text-info">{arm}</span>
      </div>
      <div className="absolute inset-x-3 bottom-2">
        <p className="font-display text-lg leading-tight text-foreground">{title}</p>
        <p className="text-[11px] text-muted-foreground">{meta}</p>
      </div>
    </div>
    <div className="space-y-2 p-3">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">
            {t}
          </span>
        ))}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground">Why this: </span>
        {why}
      </p>
    </div>
  </div>
);

/** Left-ruled callout used throughout the product for notes and warnings. */
export const Callout: React.FC<{ tone: 'info' | 'success' | 'warning' | 'destructive' | 'primary'; label?: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => {
  const rules: Record<string, string> = {
    info: 'border-l-info/60 bg-info/5',
    success: 'border-l-success/60 bg-success/5',
    warning: 'border-l-warning/60 bg-warning/5',
    destructive: 'border-l-destructive/50 bg-destructive/5',
    primary: 'border-l-primary/60 bg-primary/5',
  };
  return (
    <div className={cn('rounded-xl border-y border-r border-l-2 border-border/60 p-3 text-xs text-muted-foreground', rules[tone])}>
      {label && <span className="font-medium text-foreground">{label} </span>}
      {children}
    </div>
  );
};
